/* eslint-disable require-unicode-regexp  */
import { useMemo } from 'react';

import type { ADFEntity } from '@atlaskit/adf-utils/types';

import { generateBlockAri, generateBlockAriFromReference } from '../../clients/block-service/ari';
import {
	batchRetrieveSyncedBlocks,
	BlockError,
	createSyncedBlock,
	deleteSyncedBlock,
	getReferenceSyncedBlocks,
	getSyncedBlockContent,
	updateReferenceSyncedBlockOnDocument,
	updateSyncedBlock,
	type BlockContentErrorResponse,
	type BlockContentResponse,
} from '../../clients/block-service/blockService';
import {
	SyncBlockError,
	type ResourceId,
	type SyncBlockAttrs,
	type SyncBlockData,
	type SyncBlockProduct,
} from '../../common/types';
import { stringifyError } from '../../utils/errorHandling';
import type {
	ADFFetchProvider,
	ADFWriteProvider,
	DeleteSyncBlockResult,
	SyncBlockInstance,
	UpdateReferenceSyncBlockResult,
	WriteSyncBlockResult,
} from '../types';

const mapBlockError = (error: BlockError): SyncBlockError => {
	switch (error.status) {
		case 400:
		case 401:
			return SyncBlockError.InvalidRequest;
		case 403:
			return SyncBlockError.Forbidden;
		case 404:
			return SyncBlockError.NotFound;
		case 409:
			return SyncBlockError.Conflict;
		case 429:
			return SyncBlockError.RateLimited;
		case 500:
		case 503:
		case 504:
			return SyncBlockError.ServerError;
	}
	return SyncBlockError.Errored;
};

// convert BlockContentResponse to SyncBlockData
// throws exception if JSON parsing fails
// what's missing from BlockContentResponse to SyncBlockData:
// - updatedAt
// - sourceURL
// - sourceTitle
// - isSynced
const convertToSyncBlockData = (data: BlockContentResponse): SyncBlockData => {
	let createdAt: string | undefined;
	if (data.createdAt) {
		try {
			// BE returns microseconds, convert to milliseconds
			// BE should fix this in the future
			createdAt = new Date(data.createdAt / 1000).toISOString();
		} catch (e) {
			// fallback to undefined
			// as we don't want to block the whole process due to invalid date
			createdAt = undefined;
		}
	}

	return {
		blockInstanceId: data.blockInstanceId,
		content: JSON.parse(data.content),
		createdAt,
		createdBy: data.createdBy,
		product: data.product,
		resourceId: data.blockAri,
		sourceAri: data.sourceAri,
	};
};

export const fetchReferences = async (
	documentAri: string,
): Promise<SyncBlockInstance[] | SyncBlockError> => {
	let response: {
		blocks?: BlockContentResponse[] | undefined;
		errors?: Array<BlockContentErrorResponse>;
	};

	try {
		response = await getReferenceSyncedBlocks(documentAri);
	} catch (error) {
		if (error instanceof BlockError) {
			return mapBlockError(error);
		}

		return SyncBlockError.Errored;
	}

	const { blocks, errors } = response || {};

	const blocksInstances = (blocks || []).map((blockContentResponse) => {
		try {
			return {
				data: convertToSyncBlockData(blockContentResponse),
				resourceId: blockContentResponse.blockAri,
			} as SyncBlockInstance;
		} catch {
			// JSON parsing error, return InvalidContent error
			return {
				error: SyncBlockError.InvalidContent,
				resourceId: blockContentResponse.blockAri,
			} as SyncBlockInstance;
		}
	});

	const errorInstances = (errors || []).map(
		(errorBlock) =>
			({
				error: SyncBlockError.Errored,
				resourceId: errorBlock.blockAri,
			}) as SyncBlockInstance,
	);

	return [...blocksInstances, ...errorInstances];
};

interface BlockServiceADFFetchProviderProps {
	cloudId: string; // the cloudId of the block. E.G the cloudId of the confluence page, or the cloudId of the Jira instance
}

/**
 * ADFFetchProvider implementation that fetches synced block data from Block Service API
 */
class BlockServiceADFFetchProvider implements ADFFetchProvider {
	private cloudId: string;

	constructor({ cloudId }: BlockServiceADFFetchProviderProps) {
		this.cloudId = cloudId;
	}

	// resourceId of the reference synced block.
	// the ARI must be constructed to call the block service API
	async fetchData(resourceId: string): Promise<SyncBlockInstance> {
		const blockAri = generateBlockAriFromReference({ cloudId: this.cloudId, resourceId });

		try {
			const blockContentResponse = await getSyncedBlockContent({ blockAri });
			const value = blockContentResponse.content;

			if (!value) {
				return { error: SyncBlockError.NotFound, resourceId };
			}

			// Parse the synced block content from the response's content
			const syncedBlockData = JSON.parse(value) as Array<ADFEntity>;

			return {
				data: {
					content: syncedBlockData,
					resourceId: blockAri,
					blockInstanceId: blockContentResponse.blockInstanceId, // this was the node's localId, but has become the resourceId.
					sourceAri: blockContentResponse.sourceAri,
					product: blockContentResponse.product,
				},
				resourceId,
			};
		} catch (error) {
			if (error instanceof BlockError) {
				return { error: mapBlockError(error), resourceId };
			}
			return { error: SyncBlockError.Errored, resourceId };
		}
	}

	/**
	 * Extracts the resourceId from a block ARI.
	 * Block ARI format: ari:cloud:blocks:<cloudId>:synced-block/<resourceId>
	 */
	private extractResourceIdFromBlockAri(blockAri: string): string | undefined {
		const match = blockAri.match(/ari:cloud:blocks:[^:]+:synced-block\/(.+)$/);
		return match?.[1];
	}

	/**
	 * Batch fetches multiple synced blocks by their resource IDs.
	 * @param resourceIds - Array of resource IDs to fetch
	 * @returns Array of SyncBlockInstance results
	 */
	async batchFetchData(resourceIds: string[]): Promise<SyncBlockInstance[]> {
		const blockAris = resourceIds.map((resourceId) =>
			generateBlockAriFromReference({ cloudId: this.cloudId, resourceId }),
		);

		// Create a set of valid resourceIds for validation
		const validResourceIds = new Set(resourceIds);

		// Track which resourceIds have been processed
		const processedResourceIds = new Set<string>();

		try {
			const response = await batchRetrieveSyncedBlocks({ blockAris });
			const results: SyncBlockInstance[] = [];

			// Process successful blocks
			if (response.success) {
				for (const blockContentResponse of response.success) {
					// Extract resourceId from the returned blockAri
					const resourceId = this.extractResourceIdFromBlockAri(blockContentResponse.blockAri);
					if (!resourceId || !validResourceIds.has(resourceId)) {
						continue;
					}

					processedResourceIds.add(resourceId);

					const value = blockContentResponse.content;
					if (!value) {
						results.push({ error: SyncBlockError.NotFound, resourceId });
						continue;
					}

					try {
						const syncedBlockData = JSON.parse(value) as Array<ADFEntity>;
						results.push({
							data: {
								content: syncedBlockData,
								resourceId: blockContentResponse.blockAri,
								blockInstanceId: blockContentResponse.blockInstanceId,
								sourceAri: blockContentResponse.sourceAri,
								product: blockContentResponse.product,
							},
							resourceId,
						});
					} catch {
						results.push({ error: SyncBlockError.Errored, resourceId });
					}
				}
			}

			// Process errors
			if (response.error) {
				for (const errorResponse of response.error) {
					// Extract resourceId from the returned blockAri
					const resourceId = this.extractResourceIdFromBlockAri(errorResponse.blockAri);
					if (!resourceId || !validResourceIds.has(resourceId)) {
						continue;
					}

					processedResourceIds.add(resourceId);

					results.push({
						error: SyncBlockError.Errored,
						resourceId,
					});
				}
			}

			// Ensure all resourceIds have a result - return NotFound for any missing ones
			for (const resourceId of resourceIds) {
				if (!processedResourceIds.has(resourceId)) {
					results.push({
						error: SyncBlockError.NotFound,
						resourceId,
					});
				}
			}

			return results;
		} catch (error) {
			// If batch request fails, return error for all resourceIds
			return resourceIds.map((resourceId) => ({
				error: error instanceof BlockError ? mapBlockError(error) : SyncBlockError.Errored,
				resourceId,
			}));
		}
	}
}

interface BlockServiceADFWriteProviderProps {
	cloudId: string; // the cloudId of the block. E.G the cloudId of the confluence page, or the cloudId of the Jira instance
	getVersion?: () => number | undefined; // get the version of the block. E.G the version of the confluence page, or the version of the Jira work item
	parentAri: string; // the ARI of the parent of the block. E.G the ARI of the confluence page, or the ARI of the Jira work item
	parentId: string; // the parentId of the block. E.G the pageId for a confluence page, or the issueId for a Jira work item
	product: SyncBlockProduct; // the product of the block. E.G 'confluence-page', 'jira-work-item'
}

/**
 * ADFWriteProvider implementation that writes synced block data to Block Service API
 */
class BlockServiceADFWriteProvider implements ADFWriteProvider {
	private cloudId: string;
	private parentAri: string;
	private parentId: string;
	private getVersion?: () => number | undefined;

	product: SyncBlockProduct;

	constructor({
		cloudId,
		parentAri,
		parentId,
		product,
		getVersion,
	}: BlockServiceADFWriteProviderProps) {
		this.cloudId = cloudId;
		this.parentAri = parentAri;
		this.parentId = parentId;
		this.product = product;
		this.getVersion = getVersion;
	}

	// it will first try to update and if it can't (404) then it will try to create
	async writeData(data: SyncBlockData): Promise<WriteSyncBlockResult> {
		const { resourceId } = data;
		const blockAri = generateBlockAri({
			cloudId: this.cloudId,
			parentId: this.parentId,
			product: this.product,
			resourceId,
		});
		const stepVersion = this.getVersion ? this.getVersion() : undefined;

		try {
			// Try update existing block's content
			await updateSyncedBlock({ blockAri, content: JSON.stringify(data.content), stepVersion });
			return { resourceId };
		} catch (error) {
			if (error instanceof BlockError) {
				return { error: mapBlockError(error), resourceId };
			}
			return { error: stringifyError(error), resourceId };
		}
	}

	async createData(data: SyncBlockData): Promise<WriteSyncBlockResult> {
		const { resourceId } = data;
		const blockAri = generateBlockAri({
			cloudId: this.cloudId,
			parentId: this.parentId,
			product: this.product,
			resourceId,
		});
		const stepVersion = this.getVersion ? this.getVersion() : undefined;

		try {
			await createSyncedBlock({
				blockAri,
				blockInstanceId: data.blockInstanceId,
				sourceAri: this.parentAri,
				product: this.product,
				content: JSON.stringify(data.content),
				stepVersion,
			});

			return { resourceId };
		} catch (error) {
			if (error instanceof BlockError) {
				return { error: mapBlockError(error), resourceId };
			}
			return { error: stringifyError(error), resourceId };
		}
	}

	// soft deletes the source synced block
	async deleteData(resourceId: string): Promise<DeleteSyncBlockResult> {
		const blockAri = generateBlockAri({
			cloudId: this.cloudId,
			parentId: this.parentId,
			product: this.product,
			resourceId,
		});
		try {
			await deleteSyncedBlock({ blockAri });
			return { resourceId, success: true, error: undefined };
		} catch (error) {
			if (error instanceof BlockError) {
				if (error.status === 404) {
					// User should not be blocked by not_found error when deleting,
					// hence returns successful result for 404 error
					return { resourceId, success: true };
				}
				return { resourceId, success: false, error: mapBlockError(error) };
			}
			return { resourceId, success: false, error: stringifyError(error) };
		}
	}

	// the sourceId is the resourceId of the source synced block.
	generateResourceIdForReference(sourceId: ResourceId): ResourceId {
		return `${this.product}/${this.parentId}/${sourceId}`;
	}

	generateResourceId(): ResourceId {
		return crypto.randomUUID();
	}

	async updateReferenceData(
		blocks: SyncBlockAttrs[],
		noContent?: boolean,
	): Promise<UpdateReferenceSyncBlockResult> {
		try {
			await updateReferenceSyncedBlockOnDocument({
				documentAri: this.parentAri,
				blocks: blocks.map((block) => ({
					blockAri: generateBlockAriFromReference({
						cloudId: this.cloudId,
						resourceId: block.resourceId,
					}),
					blockInstanceId: block.localId,
				})),
				noContent,
			});
			return { success: true };
		} catch (error) {
			if (error instanceof BlockError) {
				return { success: false, error: mapBlockError(error) };
			}
			return { success: false, error: stringifyError(error) };
		}
	}
}

interface BlockServiceAPIProvidersProps {
	cloudId: string; // the cloudId of the block. E.G the cloudId of the confluence page, or the cloudId of the Jira instance
	getVersion?: () => number | undefined; // get the version of the block. E.G the version of the confluence page, or the version of the Jira work item
	parentAri: string; // the ARI of the parent of the block. E.G the ARI of the confluence page, or the ARI of the Jira work item
	parentId: string; // the parentId of the block. E.G the pageId for a confluence page, or the issueId for a Jira work item
	product: SyncBlockProduct; // the product of the block. E.G 'confluence-page', 'jira-work-item'
}
export const createBlockServiceAPIProviders = ({
	cloudId,
	parentAri,
	parentId,
	product,
	getVersion,
}: BlockServiceAPIProvidersProps): {
	fetchProvider: BlockServiceADFFetchProvider;
	writeProvider: BlockServiceADFWriteProvider;
} => {
	return {
		fetchProvider: new BlockServiceADFFetchProvider({ cloudId }),
		writeProvider: new BlockServiceADFWriteProvider({
			cloudId,
			parentAri,
			parentId,
			product,
			getVersion,
		}),
	};
};

export const useMemoizedBlockServiceAPIProviders = ({
	cloudId,
	parentAri,
	parentId,
	product,
	getVersion,
}: BlockServiceAPIProvidersProps): {
	fetchProvider: BlockServiceADFFetchProvider;
	writeProvider: BlockServiceADFWriteProvider;
} => {
	return useMemo(() => {
		return createBlockServiceAPIProviders({ cloudId, parentAri, parentId, product, getVersion });
	}, [cloudId, parentAri, parentId, product, getVersion]);
};

interface BlockServiceFetchOnlyAPIProviderProps {
	cloudId: string; // the cloudId of the block. E.G the cloudId of the confluence page, or the cloudId of the Jira instance
}

export const createBlockServiceFetchOnlyAPIProvider = ({
	cloudId,
}: BlockServiceFetchOnlyAPIProviderProps): {
	fetchProvider: BlockServiceADFFetchProvider;
	writeProvider: undefined;
} => {
	return {
		fetchProvider: new BlockServiceADFFetchProvider({ cloudId }),
		writeProvider: undefined,
	};
};

export const useMemoizedBlockServiceFetchOnlyAPIProvider = ({
	cloudId,
}: BlockServiceFetchOnlyAPIProviderProps): {
	fetchProvider: BlockServiceADFFetchProvider;
	writeProvider: undefined;
} => useMemo(() => createBlockServiceFetchOnlyAPIProvider({ cloudId }), [cloudId]);
